import React, { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  message,
  Popconfirm,
  Tooltip,
  Typography,
  Alert,
  Avatar,
  Badge,
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
  EyeOutlined,
  EditOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store'
import {
  fetchUsers,
  updateUserStatus,
  batchUpdateStatus,
  fetchStatistics,
  setFilters,
  setSelectedUsers,
  clearSelected,
} from '@store/slices/userSlice'
import { userService } from '@services/userService'
import dayjs from 'dayjs'

const { Title } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

const UserManagement: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { users, loading, error, filters, pagination, selectedUsers, statistics } = useAppSelector(
    (state) => state.users
  )

  const [searchText, setSearchText] = useState('')
  const [exportLoading, setExportLoading] = useState(false)
  const [batchModalVisible, setBatchModalVisible] = useState(false)
  const [batchStatus, setBatchStatus] = useState<'active' | 'inactive' | 'banned'>('active')

  useEffect(() => {
    dispatch(fetchUsers(filters))
    dispatch(fetchStatistics(filters))
  }, [dispatch, filters])

  // 处理搜索
  const handleSearch = () => {
    const searchFilters = { 
      ...filters, 
      search: searchText,
      page: 1 
    }
    dispatch(setFilters(searchFilters))
    dispatch(fetchUsers(searchFilters))
  }

  // 处理重置
  const handleReset = () => {
    const resetFilters = {
      status: 'all' as const,
      city: '',
      province: '',
      startDate: '',
      endDate: '',
      page: 1,
      pageSize: 10,
    }
    dispatch(setFilters(resetFilters))
    dispatch(fetchUsers(resetFilters))
    setSearchText('')
  }

  // 处理分页
  const handleTableChange = (page: number, pageSize: number) => {
    dispatch(setFilters({ ...filters, page, pageSize }))
    dispatch(fetchUsers({ ...filters, page, pageSize }))
  }

  // 处理状态更新
  const handleStatusUpdate = async (id: string, status: 'active' | 'inactive' | 'banned') => {
    try {
      await dispatch(updateUserStatus({ id, status })).unwrap()
      message.success('状态更新成功')
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '状态更新失败')
    }
  }

  // 处理批量状态更新
  const handleBatchStatusUpdate = async () => {
    if (selectedUsers.length === 0) {
      message.warning('请选择要更新的用户')
      return
    }

    try {
      await dispatch(batchUpdateStatus({ ids: selectedUsers, status: batchStatus })).unwrap()
      message.success('批量更新成功')
      setBatchModalVisible(false)
      dispatch(clearSelected())
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '批量更新失败')
    }
  }

  // 处理导出
  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    setExportLoading(true)
    try {
      await userService.exportUsers(filters, format)
      message.success('已开始下载 CSV 文件')
    } catch (error: any) {
      message.error(error?.message || error || '导出失败')
    } finally {
      setExportLoading(false)
    }
  }

  // 获取状态标签
  const getStatusTag = (status: string) => {
    const statusMap = {
      active: { color: 'green', text: '正常' },
      inactive: { color: 'orange', text: '未激活' },
      banned: { color: 'red', text: '已封禁' },
    }
    const config = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  // 获取性别标签
  const getGenderTag = (gender: number) => {
    const genderMap = {
      1: { color: 'blue', text: '男' },
      2: { color: 'pink', text: '女' },
    }
    const config = genderMap[gender as keyof typeof genderMap] || { color: 'default', text: '未知' }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  // 表格列定义
  const columns = [
    {
      title: '用户信息',
      key: 'userInfo',
      width: 220,
      render: (_, record: any) => (
        <Space>
          <Avatar
            src={record.avatar || record.avatarUrl}
            icon={<UserOutlined />}
            size="small"
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.nickname || '未设置昵称'}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              微信号: {record.openid ? record.openid.slice(0, 16) + '...' : '未知'}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '联系方式',
      key: 'contact',
      width: 150,
      render: (_, record: any) => (
        <div>
          <div style={{ fontSize: '14px' }}>
            <PhoneOutlined style={{ marginRight: '4px' }} />
            {record.phone || '未设置'}
          </div>
        </div>
      ),
    },
    {
      title: '地区',
      key: 'region',
      width: 120,
      render: (_, record: any) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <EnvironmentOutlined style={{ marginRight: '4px' }} />
            {record.province} {record.city}
          </div>
        </div>
      ),
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
      render: (gender: number) => getGenderTag(gender),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '消费统计',
      key: 'consumption',
      width: 120,
      render: (_, record: any) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <strong>¥{record.totalSpent || 0}</strong>
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            {record.totalOrders || 0}单
          </div>
        </div>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '最后登录时间',
      key: 'lastLoginAt',
      width: 120,
      render: (_, record: any) => (
        <div style={{ fontSize: '12px' }}>
          {record.lastLoginAt ?
            dayjs(record.lastLoginAt).format('MM-DD HH:mm') :
            '从未登录'
          }
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record: any) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => navigate(`/users/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button type="link" icon={<EditOutlined />} size="small" />
          </Tooltip>
          {record.status === 'active' && (
            <Popconfirm
              title="确定要封禁这个用户吗？"
              onConfirm={() => handleStatusUpdate(record.id, 'banned')}
            >
              <Tooltip title="封禁用户">
                <Button type="link" icon={<CloseCircleOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
          {record.status === 'banned' && (
            <Popconfirm
              title="确定要解封这个用户吗？"
              onConfirm={() => handleStatusUpdate(record.id, 'active')}
            >
              <Tooltip title="解封用户">
                <Button type="link" icon={<CheckCircleOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
          {record.status === 'inactive' && (
            <Popconfirm
              title="确定要激活这个用户吗？"
              onConfirm={() => handleStatusUpdate(record.id, 'active')}
            >
              <Tooltip title="激活用户">
                <Button type="link" icon={<CheckCircleOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          用户管理
        </Title>
        <Typography.Text type="secondary">
          管理所有用户信息，包括状态控制、数据导出等功能
        </Typography.Text>
      </div>

      {error && (
        <Alert
          message="数据加载失败"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="总用户数" value={statistics.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="正常用户" value={statistics.active} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="未激活" value={statistics.inactive} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="已封禁" value={statistics.banned} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      {/* 筛选和操作区域 */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="搜索用户昵称或手机号"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.status}
              onChange={(value) => dispatch(setFilters({ ...filters, status: value }))}
              style={{ width: '100%' }}
            >
              <Option value="all">全部状态</Option>
              <Option value="active">正常</Option>
              <Option value="inactive">未激活</Option>
              <Option value="banned">已封禁</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.province}
              onChange={(value) => dispatch(setFilters({ ...filters, province: value }))}
              style={{ width: '100%' }}
              placeholder="选择省份"
            >
              <Option value="">全部省份</Option>
              <Option value="北京市">北京市</Option>
              <Option value="上海市">上海市</Option>
              <Option value="广东省">广东省</Option>
              <Option value="浙江省">浙江省</Option>
              <Option value="江苏省">江苏省</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
              onChange={(dates) => {
                if (dates) {
                  dispatch(setFilters({
                    ...filters,
                    startDate: dates[0]?.format('YYYY-MM-DD') || '',
                    endDate: dates[1]?.format('YYYY-MM-DD') || '',
                  }))
                } else {
                  dispatch(setFilters({ ...filters, startDate: '', endDate: '' }))
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          
          {/* 第二行：操作按钮 */}
          <Col xs={24}>
            <Space wrap>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                icon={<ExportOutlined />}
                loading={exportLoading}
                onClick={() => handleExport('excel')}
              >
                导出
              </Button>
              {selectedUsers.length > 0 && (
                <Button
                  type="primary"
                  danger
                  onClick={() => setBatchModalVisible(true)}
                >
                  批量操作 ({selectedUsers.length})
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 用户列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
          rowSelection={{
            selectedRowKeys: selectedUsers,
            onChange: (selectedRowKeys) => {
              dispatch(setSelectedUsers(selectedRowKeys as string[]))
            },
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 批量操作模态框 */}
      <Modal
        title="批量操作"
        open={batchModalVisible}
        onOk={handleBatchStatusUpdate}
        onCancel={() => setBatchModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <p>已选择 {selectedUsers.length} 个用户</p>
        <p>请选择要更新的状态：</p>
        <Select
          value={batchStatus}
          onChange={setBatchStatus}
          style={{ width: '100%' }}
        >
          <Option value="active">激活</Option>
          <Option value="inactive">设为未激活</Option>
          <Option value="banned">封禁</Option>
        </Select>
      </Modal>
    </div>
  )
}

export default UserManagement
